import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { queryKeys } from '../lib/queryKeys.js';
import { useSession } from '../stores/session.js';
import { toast } from '../stores/ui.js';

/**
 * Establishing and ending an admin session.
 *
 * The permission list comes back with the session on every read, so a role
 * changed by an administrator takes effect on this operator's next window focus
 * rather than at their next sign-in.
 */
export const useEstablishSession = () => {
  const signedIn = useSession((state) => state.signedIn);
  const signedOut = useSession((state) => state.signedOut);

  return useQuery({
    queryKey: queryKeys.session(),
    queryFn: async () => {
      try {
        const result = await api.get('/admin/auth/me');
        signedIn(result.admin, result.permissions);
        return result;
      } catch {
        signedOut();
        return null;
      }
    },
    retry: false,
    staleTime: 60 * 1000,
  });
};

export const useLogin = () => {
  const client = useQueryClient();
  const signedIn = useSession((state) => state.signedIn);

  return useMutation({
    mutationFn: (credentials) => api.post('/admin/auth/login', credentials),
    onSuccess: async (result) => {
      // The login response carries the account but not the expanded permission
      // set, so the session read follows immediately rather than guessing.
      const session = await api.get('/admin/auth/me');
      signedIn(session.admin, session.permissions);
      client.setQueryData(queryKeys.session(), session);
      return result;
    },
  });
};

export const useLogout = () => {
  const client = useQueryClient();
  const signedOut = useSession((state) => state.signedOut);

  return useMutation({
    mutationFn: () => api.post('/admin/auth/logout'),
    onSuccess: () => {
      signedOut();
      client.clear();
      toast({ title: 'Signed out' });
    },
  });
};

export const useChangePassword = () =>
  useMutation({
    mutationFn: (values) => api.post('/admin/auth/password', values),
  });
